import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { AuthInput, AuthPasswordInput, AuthButton } from "./AuthLayout";
import { register as registerApi } from "../api/auth";

export type ERROR_TYPE = "USER_EMAIL_EXISTS" | "AUTH_RATE_LIMITED" | "VALIDATION_FAILED";

export function RegisterForm({ onLoginClick, onSuccess }: { onLoginClick: () => void, onSuccess: (email: string) => void }) {
    const { t } = useTranslation();

    const schema = z.object({
        first_name: z.string().min(1, t('auth.register.errors.firstNameRequired')),
        last_name: z.string().min(1, t('auth.register.errors.lastNameRequired')),
        username: z.string().min(3, t('auth.register.errors.usernameMin')).optional().or(z.literal("")),
        email: z.string().email(t('auth.register.errors.invalidEmail')),
        password: z.string()
            .min(8, t('auth.register.errors.passwordMin'))
            .regex(/[a-z]/, t('auth.register.errors.passwordLowercase'))
            .regex(/[A-Z]/, t('auth.register.errors.passwordUppercase')),
        confirm_password: z.string().min(1, t('auth.register.errors.confirmPasswordRequired')),
    }).refine((data) => data.password === data.confirm_password, {
        message: t('auth.register.errors.passwordMismatch'),
        path: ["confirm_password"],
    });

    type FormData = z.infer<typeof schema>;

    const { register, handleSubmit, formState: { errors }, setError } = useForm<FormData>({
        resolver: zodResolver(schema),
    });
    const [loading, setLoading] = useState(false);

    const onSubmit = async (data: FormData) => {
        setLoading(true);
        try {
            await registerApi({ ...data, username: data.username || undefined });
            onSuccess(data.email);
        } catch (errorMessage: any) {
            const errorCode = errorMessage?.response?.data?.error?.code as ERROR_TYPE;

            if (errorCode === 'USER_EMAIL_EXISTS') {
                setError("root", { message: t('auth.register.errors.emailExists') });

            } else if (errorCode === 'AUTH_RATE_LIMITED') {
                setError("root", { message: t('auth.register.errors.rateLimited') });
            } else if (errorCode === 'VALIDATION_FAILED') {
                setError("root", { message: t('auth.register.errors.validationFailed') });
            }
            else {
                setError("root", { message: t('auth.register.errors.registrationFailed') });
            }

        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col items-center max-w-[600px]">
            <h1 className="font-script text-6xl text-[#338585] mb-2">{t('auth.register.title')}</h1>
            <p className="text-gray-500 mb-8">{t('auth.register.subtitle')}</p>

            {errors.root && (
                <div className="w-full mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                            <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-medium text-red-800">
                                {errors.root.message}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-x-4">
                <AuthInput
                    placeholder={t('auth.register.firstNamePlaceholder')}
                    {...register("first_name")}
                    error={errors.first_name?.message}
                />
                <AuthInput
                    placeholder={t('auth.register.lastNamePlaceholder')}
                    {...register("last_name")}
                    error={errors.last_name?.message}
                />

                <AuthInput
                    placeholder={t('auth.register.usernamePlaceholder')}
                    {...register("username")}
                    error={errors.username?.message}
                />
                <AuthInput
                    placeholder={t('auth.register.emailPlaceholder')}
                    {...register("email")}
                    error={errors.email?.message}
                />

                <AuthPasswordInput
                    placeholder={t('auth.register.passwordPlaceholder')}
                    {...register("password")}
                    error={errors.password?.message}
                />
                <AuthPasswordInput
                    placeholder={t('auth.register.confirmPasswordPlaceholder')}
                    {...register("confirm_password")}
                    error={errors.confirm_password?.message}
                />
            </div>

            {/* Password Requirements */}
            <div className="w-full mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
                <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                        <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div className="flex-1">
                        <h4 className="text-sm font-semibold text-blue-900 mb-2">{t('auth.register.passwordRequirements.title')}</h4>
                        <ul className="text-xs text-blue-800 space-y-1">
                            <li className="flex items-center gap-2">
                                <span className="w-1 h-1 bg-blue-600 rounded-full"></span>
                                {t('auth.register.passwordRequirements.minLength')}
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="w-1 h-1 bg-blue-600 rounded-full"></span>
                                {t('auth.register.passwordRequirements.uppercase')}
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="w-1 h-1 bg-blue-600 rounded-full"></span>
                                {t('auth.register.passwordRequirements.lowercase')}
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="w-1 h-1 bg-blue-600 rounded-full"></span>
                                {t('auth.register.passwordRequirements.mustMatch')}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <AuthButton disabled={loading}>
                {loading ? t('auth.register.registering') : t('auth.register.registerButton')}
            </AuthButton>

            <div className="mt-6 text-sm text-gray-600">
                {t('auth.register.alreadyHaveAccount')} <button type="button" onClick={onLoginClick} className="text-[#E86C31] hover:underline ml-1">{t('auth.register.loginNow')}</button>
            </div>
        </form>
    );
}
